import { useState, useEffect, useRef, useCallback } from 'react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { 
  Video, VideoOff, Mic, MicOff, Users, Send, ShoppingBag, 
  Clock, Play, Square, Calendar, Eye, MessageCircle, Tag,
  ChevronRight, Radio, Check
} from 'lucide-react';
import { isHeicFile, convertHeicToJpeg, ACCEPTED_IMAGE_FORMATS } from '@/utils/imageUtils';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Stream Broadcaster Component (for hosts)
export const StreamBroadcaster = ({ 
  streamId, 
  channelName, 
  token, 
  appId, 
  uid, 
  onStreamStart, 
  onStreamEnd,
  products = []
}) => {
  const [isLive, setIsLive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [highlightedProduct, setHighlightedProduct] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  
  const clientRef = useRef(null);
  const localVideoRef = useRef(null);
  const audioTrackRef = useRef(null);
  const videoTrackRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    // Initialize Agora client
    clientRef.current = AgoraRTC.createClient({ mode: 'live', codec: 'vp9' });
    
    // Add connection state change handler
    clientRef.current.on('connection-state-change', (curState, prevState, reason) => {
      console.log('Agora connection state:', prevState, '->', curState, 'reason:', reason);
    });
    
    return () => {
      // Cleanup without triggering onStreamEnd callback
      const cleanup = async () => {
        try {
          if (audioTrackRef.current) {
            audioTrackRef.current.stop();
            audioTrackRef.current.close();
          }
          if (videoTrackRef.current) {
            videoTrackRef.current.stop();
            videoTrackRef.current.close();
          }
          if (clientRef.current) {
            await clientRef.current.leave();
          }
        } catch (e) {
          console.log('Cleanup error:', e);
        }
      };
      cleanup();
    };
  }, []);

  useEffect(() => {
    if (isLive && streamId) {
      // Connect to WebSocket for chat
      const wsUrl = `${API.replace('http', 'ws').replace('/api', '')}/api/live/ws/${streamId}?user_id=${uid}&user_name=Host`;
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'viewer_joined' || data.type === 'viewer_left') {
          setViewerCount(data.viewer_count);
        }
      };
      
      return () => {
        if (wsRef.current) {
          wsRef.current.close();
        }
      };
    }
  }, [isLive, streamId, uid]);

  const startStream = async () => {
    console.log('Go Live button clicked!');
    setIsConnecting(true);
    
    // Set a timeout to prevent hanging forever
    const timeoutId = setTimeout(() => {
      console.error('Connection timeout - took too long');
      alert('Connection timed out. Please check your camera/microphone permissions and try again.');
      setIsConnecting(false);
    }, 20000); // 20 second timeout
    
    try {
      const client = clientRef.current;
      
      if (!client) {
        clearTimeout(timeoutId);
        console.error('Agora client not initialized');
        alert('Error: Agora client not initialized. Please refresh the page.');
        setIsConnecting(false);
        return;
      }
      
      if (!appId || !token || !channelName) {
        clearTimeout(timeoutId);
        console.error('Missing Agora credentials:', { appId: !!appId, token: !!token, channelName: !!channelName });
        alert('Error: Missing streaming credentials. Please refresh the page.');
        setIsConnecting(false);
        return;
      }
      
      console.log('Step 1: Requesting camera and microphone access...');
      
      // FIRST: Request camera/mic access before joining Agora
      // This ensures the permission popup appears immediately
      let audioTrack, videoTrack;
      try {
        [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks();
        console.log('Step 2: Camera and microphone access granted!');
      } catch (mediaError) {
        clearTimeout(timeoutId);
        console.error('Failed to access camera/microphone:', mediaError);
        
        // Provide specific error messages
        if (mediaError.code === 'PERMISSION_DENIED' || mediaError.message?.includes('Permission') || mediaError.message?.includes('denied')) {
          alert('🚫 Camera/microphone access was denied.\n\nPlease:\n1. Click the camera icon in your browser address bar\n2. Allow access to camera and microphone\n3. Click "Go Live" again');
        } else if (mediaError.code === 'NOT_FOUND' || mediaError.message?.includes('not found')) {
          alert('📷 No camera or microphone found.\n\nPlease connect a camera and microphone, then try again.');
        } else {
          alert(`❌ Failed to access camera/microphone:\n${mediaError.message || 'Unknown error'}\n\nPlease check your device settings.`);
        }
        setIsConnecting(false);
        return;
      }
      
      audioTrackRef.current = audioTrack;
      videoTrackRef.current = videoTrack;
      
      console.log('Step 3: Setting role to host...');
      await client.setClientRole('host');
      
      console.log('Step 4: Joining Agora channel...');
      await client.join(appId, channelName, token, uid);
      console.log('Step 5: Joined Agora channel successfully');
      
      // Play local video preview
      if (localVideoRef.current) {
        videoTrack.play(localVideoRef.current);
        console.log('Step 6: Playing local video preview');
      }
      
      // Publish tracks to broadcast
      console.log('Step 7: Publishing tracks...');
      await client.publish([audioTrack, videoTrack]);
      console.log('✅ SUCCESS: You are now LIVE!');
      
      clearTimeout(timeoutId);
      setIsLive(true);
      setIsConnecting(false);
      onStreamStart?.();
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Error starting stream:', error);
      
      // Clean up tracks if created
      if (audioTrackRef.current) {
        audioTrackRef.current.stop();
        audioTrackRef.current.close();
        audioTrackRef.current = null;
      }
      if (videoTrackRef.current) {
        videoTrackRef.current.stop();
        videoTrackRef.current.close();
        videoTrackRef.current = null;
      }
      
      alert(`❌ Failed to start stream:\n${error.message || 'Unknown error'}\n\nPlease refresh the page and try again.`);
      setIsConnecting(false);
    }
  };

  const stopStream = async () => {
    try {
      // Stop and close tracks
      if (audioTrackRef.current) {
        audioTrackRef.current.stop();
        audioTrackRef.current.close();
      }
      if (videoTrackRef.current) {
        videoTrackRef.current.stop();
        videoTrackRef.current.close();
      }
      
      // Leave channel
      if (clientRef.current) {
        await clientRef.current.leave();
      }
      
      setIsLive(false);
      onStreamEnd?.();
    } catch (error) {
      console.error('Error stopping stream:', error);
    }
  };

  const toggleMute = () => {
    if (audioTrackRef.current) {
      audioTrackRef.current.setEnabled(isMuted);
      setIsMuted(!isMuted);
    }
  };

  const toggleVideo = () => {
    if (videoTrackRef.current) {
      videoTrackRef.current.setEnabled(isVideoOff);
      setIsVideoOff(!isVideoOff);
    }
  };

  const highlightProduct = (product) => {
    setHighlightedProduct(product);
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'highlight_product',
        listing_id: product.listing_id
      }));
    }
  };

  return (
    <div className="relative">
      {/* Video Container */}
      <div 
        ref={localVideoRef} 
        className="w-full aspect-video bg-black rounded-lg overflow-hidden"
      >
        {!isLive && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
            <div className="text-center text-white">
              <Video className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">Click &quot;Go Live&quot; to start streaming</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Live Badge & Viewer Count */}
      {isLive && (
        <div className="absolute top-4 left-4 flex items-center gap-3">
          <Badge className="bg-red-500 text-white animate-pulse">
            <Radio className="h-3 w-3 mr-1" /> LIVE
          </Badge>
          <Badge variant="secondary" className="bg-black/50 text-white">
            <Users className="h-3 w-3 mr-1" /> {viewerCount}
          </Badge>
        </div>
      )}
      
      {/* Product Highlight Overlay */}
      {isLive && highlightedProduct && (
        <div className="absolute bottom-20 left-4 right-4 bg-white/95 backdrop-blur rounded-lg p-3 shadow-lg animate-in slide-in-from-bottom">
          <div className="flex items-center gap-3">
            {highlightedProduct.image && (
              <img src={highlightedProduct.image} alt="" className="w-16 h-16 rounded-lg object-cover" />
            )}
            <div className="flex-1">
              <p className="font-semibold text-sm line-clamp-1">{highlightedProduct.title}</p>
              <p className="text-emerald-600 font-bold">${highlightedProduct.price?.toLocaleString()}</p>
            </div>
            <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600">
              <ShoppingBag className="h-4 w-4 mr-1" /> Buy
            </Button>
          </div>
        </div>
      )}
      
      {/* Controls */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-2 bg-black/70 backdrop-blur rounded-full px-4 py-2">
        <Button 
          size="icon" 
          variant={isMuted ? "destructive" : "secondary"}
          onClick={toggleMute}
          disabled={!isLive}
          className="rounded-full"
        >
          {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </Button>
        
        <Button 
          size="icon" 
          variant={isVideoOff ? "destructive" : "secondary"}
          onClick={toggleVideo}
          disabled={!isLive}
          className="rounded-full"
        >
          {isVideoOff ? <VideoOff className="h-4 w-4" /> : <Video className="h-4 w-4" />}
        </Button>
        
        {!isLive ? (
          <Button 
            onClick={startStream} 
            className="bg-red-500 hover:bg-red-600 rounded-full px-6"
            disabled={isConnecting}
          >
            {isConnecting ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                Connecting...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" /> Go Live
              </>
            )}
          </Button>
        ) : (
          <Button onClick={stopStream} variant="destructive" className="rounded-full px-6">
            <Square className="h-4 w-4 mr-2" /> End Stream
          </Button>
        )}
      </div>
      
      {/* Products Panel (for host) */}
      {isLive && products.length > 0 && (
        <div className="absolute top-4 right-4 bg-black/70 backdrop-blur rounded-lg p-2 max-h-60 overflow-y-auto">
          <p className="text-white text-xs mb-2 px-2">Tap to highlight:</p>
          {products.map((product) => (
            <button
              key={product.listing_id}
              onClick={() => highlightProduct(product)}
              className={`w-full flex items-center gap-2 p-2 rounded-lg hover:bg-white/20 transition ${
                highlightedProduct?.listing_id === product.listing_id ? 'bg-white/30' : ''
              }`}
            >
              {product.image && (
                <img src={product.image} alt="" className="w-10 h-10 rounded object-cover" />
              )}
              <div className="text-left text-white">
                <p className="text-xs line-clamp-1">{product.title}</p>
                <p className="text-xs text-emerald-400">${product.price}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Stream Viewer Component (for viewers)
export const StreamViewer = ({ 
  streamId, 
  channelName, 
  token, 
  appId, 
  uid,
  hostName,
  products = [],
  onPurchase,
  thumbnail,
  category,
  isMock = false
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [hasRemoteVideo, setHasRemoteVideo] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [highlightedProduct, setHighlightedProduct] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [connectionError, setConnectionError] = useState(false);
  
  const clientRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const wsRef = useRef(null);
  const timeoutRef = useRef(null);

  // Check if this is a mock stream
  const isMockStream = isMock || streamId?.startsWith('mock_');
  
  // State for video loading/error handling
  const [videoError, setVideoError] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const [youtubeVideo, setYoutubeVideo] = useState(null);
  const [embedFailed, setEmbedFailed] = useState(false);
  const iframeRef = useRef(null);
  
  // Listen for YouTube player errors via postMessage
  useEffect(() => {
    if (!isMockStream || !youtubeVideo) return;
    
    const handleYouTubeMessage = (event) => {
      // Check if message is from YouTube
      if (event.origin !== 'https://www.youtube.com') return;
      
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        // Check for error events from YouTube iframe API
        if (data.event === 'onError' || 
            (data.info && data.info.playerState === -1) ||
            (data.event === 'onStateChange' && data.info === -1)) {
          console.log('YouTube player error detected:', data);
          setEmbedFailed(true);
        }
      } catch (e) {
        // Not a JSON message, ignore
      }
    };
    
    window.addEventListener('message', handleYouTubeMessage);
    
    // Also set a timeout - if video hasn't played after 8 seconds, offer fallback option
    const timeoutId = setTimeout(() => {
      // Check if video is visible by verifying the iframe exists and is in document
      if (iframeRef.current && !embedFailed) {
        // Don't auto-fail, but ensure UI shows escape options
        console.log('Video load timeout - escape options available');
      }
    }, 8000);
    
    return () => {
      window.removeEventListener('message', handleYouTubeMessage);
      clearTimeout(timeoutId);
    };
  }, [isMockStream, youtubeVideo, embedFailed]);
  
  // Function to fetch a new video (called by Skip/Try Another buttons)
  const fetchNewVideo = async () => {
    try {
      setEmbedFailed(false);
      setYoutubeVideo(null);
      setVideoLoading(true);
      
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const timestamp = Date.now();
      const response = await fetch(`${API_URL}/api/live/streams/${streamId}/video?t=${timestamp}&force=true`);
      
      if (response.ok) {
        const video = await response.json();
        if (video && video.video_id) {
          setYoutubeVideo(video);
          setVideoLoading(false);
        }
      }
    } catch (error) {
      console.log('Could not fetch new video:', error);
      setVideoLoading(false);
    }
  };

  // Fetch real YouTube video from API - only once per stream, no auto-refresh
  useEffect(() => {
    if (!isMockStream) return;
    
    const fetchYouTubeVideo = async () => {
      try {
        // Reset embed failure when fetching new video
        setEmbedFailed(false);
        setVideoLoading(true);
        
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        // Add timestamp to bypass cache
        const timestamp = Date.now();
        const response = await fetch(`${API_URL}/api/live/streams/${streamId}/video?t=${timestamp}&force=true`);
        
        if (response.ok) {
          const video = await response.json();
          if (video && video.video_id) {
            setYoutubeVideo(video);
            setVideoLoading(false);
          }
        } else {
          // Fallback to category-based search
          const categoryParam = category || 'shopping';
          const fallbackResponse = await fetch(`${API_URL}/api/youtube/videos?category=${categoryParam}&max_results=1&video_type=live`);
          
          if (fallbackResponse.ok) {
            const data = await fallbackResponse.json();
            if (data.videos && data.videos.length > 0) {
              setYoutubeVideo(data.videos[0]);
              setVideoLoading(false);
            }
          }
        }
      } catch (error) {
        console.log('Could not fetch YouTube video:', error);
        setVideoLoading(false);
      }
    };
    
    // Fetch video only once when component mounts or stream changes
    fetchYouTubeVideo();
    
    // No auto-refresh - video stays until user clicks "Skip" or stream changes
  }, [isMockStream, streamId, category]);
  
  // Get video URL - use fetched YouTube video or fallback
  const getVideoUrl = () => {
    if (youtubeVideo) {
      return {
        type: 'youtube',
        url: youtubeVideo.embed_url,
        watchUrl: youtubeVideo.watch_url,
        videoId: youtubeVideo.video_id,
        title: youtubeVideo.title,
        thumbnail: youtubeVideo.thumbnail,
        channelTitle: youtubeVideo.channel_title
      };
    }
    
    // Fallback to static videos if API fails
    const fallbackVideos = {
      'collectibles': { id: 'jfKfPfyJRdk', title: 'lofi hip hop radio' },
      'fashion': { id: '5qap5aO4i9A', title: 'lofi hip hop radio' },
      'electronics': { id: 'jfKfPfyJRdk', title: 'lofi hip hop radio' },
      'default': { id: 'jfKfPfyJRdk', title: 'lofi hip hop radio' }
    };
    
    const videoData = fallbackVideos[category?.toLowerCase()] || fallbackVideos['default'];
    
    return { 
      type: 'youtube', 
      url: `https://www.youtube.com/embed/${videoData.id}?autoplay=1&mute=0&controls=1`,
      watchUrl: `https://www.youtube.com/watch?v=${videoData.id}`,
      videoId: videoData.id,
      title: videoData.title,
      thumbnail: `https://img.youtube.com/vi/${videoData.id}/maxresdefault.jpg`
    };
  };
  
  // Real-time viewer count from backend
  const [realViewerCount, setRealViewerCount] = useState(0);
  
  useEffect(() => {
    if (!streamId) return;
    
    // Fetch initial viewer count
    const fetchViewerCount = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/live/streams/${streamId}/viewers`);
        if (response.ok) {
          const data = await response.json();
          setRealViewerCount(data.viewer_count || 0);
        }
      } catch (error) {
        console.log('Could not fetch viewer count');
      }
    };
    
    fetchViewerCount();
    
    // Poll for viewer count updates every 10 seconds
    const interval = setInterval(fetchViewerCount, 10000);
    
    // Increment viewer count when joining
    const incrementViewer = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        await fetch(`${API_URL}/api/live/streams/${streamId}/join`, { method: 'POST' });
      } catch (error) {
        console.log('Could not increment viewer');
      }
    };
    incrementViewer();
    
    return () => {
      clearInterval(interval);
      // Decrement viewer count when leaving
      const decrementViewer = async () => {
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          await fetch(`${API_URL}/api/live/streams/${streamId}/leave`, { method: 'POST' });
        } catch (error) {
          console.log('Could not decrement viewer');
        }
      };
      decrementViewer();
    };
  }, [streamId]);
  
  // Initialize state for mock streams
  const [mockViewerCount] = useState(() => 
    isMockStream ? Math.floor(Math.random() * 200) + 50 : 0
  );

  // Reset video error state when stream changes
  useEffect(() => {
    setVideoError(false);
    setVideoLoading(true);
    setEmbedFailed(false);
  }, [streamId]);

  useEffect(() => {
    // For mock streams, don't try to connect to Agora
    if (isMockStream) {
      setIsConnected(true);
      setViewerCount(mockViewerCount);
      return;
    }

    // Skip if no credentials
    if (!appId || !token || !channelName) {
      console.log('Missing Agora credentials for viewer');
      return;
    }

    let isCancelled = false;
    
    // Initialize Agora client
    const client = AgoraRTC.createClient({ mode: 'live', codec: 'vp9' });
    clientRef.current = client;
    
    const setupViewer = async () => {
      try {
        // Set role to audience
        await client.setClientRole('audience');
        
        // Listen for remote user
        client.on('user-published', async (user, mediaType) => {
          if (isCancelled) return;
          await client.subscribe(user, mediaType);
          setHasRemoteVideo(true);
          
          if (mediaType === 'video' && remoteVideoRef.current) {
            user.videoTrack?.play(remoteVideoRef.current);
          }
          
          if (mediaType === 'audio') {
            user.audioTrack?.play();
          }
        });
        
        client.on('user-unpublished', (user, mediaType) => {
          if (isCancelled) return;
          if (mediaType === 'video') {
            setHasRemoteVideo(false);
            if (remoteVideoRef.current) {
              remoteVideoRef.current.innerHTML = '';
            }
          }
        });
        
        // Join channel
        if (!isCancelled) {
          await client.join(appId, channelName, token, uid);
          if (!isCancelled) {
            setIsConnected(true);
            console.log('Viewer joined Agora channel successfully');
          }
        }
        
        // Set timeout to show "host offline" message if no video after 10s
        if (!isCancelled) {
          timeoutRef.current = setTimeout(() => {
            if (!hasRemoteVideo) {
              setConnectionError(true);
            }
          }, 10000);
        }
        
      } catch (error) {
        if (!isCancelled) {
          console.error('Error joining stream:', error);
          setConnectionError(true);
        }
      }
    };

    setupViewer();
    
    return () => {
      isCancelled = true;
      if (clientRef.current) {
        clientRef.current.leave().catch(() => {});
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [appId, channelName, token, uid, isMockStream, mockViewerCount]);

  useEffect(() => {
    // Connect to WebSocket for chat
    const wsUrl = `${API.replace('http', 'ws').replace('/api', '')}/api/live/ws/${streamId}?user_id=${uid}&user_name=Viewer`;
    wsRef.current = new WebSocket(wsUrl);
    
    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'chat') {
        setChatMessages(prev => [...prev.slice(-49), data]);
      } else if (data.type === 'viewer_joined' || data.type === 'viewer_left') {
        setViewerCount(data.viewer_count);
      } else if (data.type === 'highlight_product') {
        const product = products.find(p => p.listing_id === data.listing_id);
        setHighlightedProduct(product);
      }
    };
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [streamId, uid, products]);

  const sendMessage = () => {
    if (!chatInput.trim() || !wsRef.current) return;
    
    wsRef.current.send(JSON.stringify({
      type: 'chat',
      content: chatInput,
      sender_name: 'Viewer'
    }));
    
    setChatInput('');
  };

  return (
    <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden">
      {/* Mock Stream - YouTube Video Player - Auto-plays immediately */}
      {isMockStream && (
        <div className="absolute inset-0 bg-slate-900">
          {/* Loading state while fetching video */}
          {videoLoading && !youtubeVideo && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900 z-20">
              <div className="text-white text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                <p>Loading live stream...</p>
              </div>
            </div>
          )}
          
          {/* Embed failed - Show graceful fallback with thumbnail and "Watch on YouTube" button */}
          {embedFailed && youtubeVideo && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900 z-20">
              <div className="relative w-full h-full">
                {/* Thumbnail background */}
                <img 
                  src={getVideoUrl().thumbnail || `https://img.youtube.com/vi/${getVideoUrl().videoId}/maxresdefault.jpg`}
                  alt={getVideoUrl().title}
                  className="w-full h-full object-cover opacity-60"
                />
                {/* Overlay content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50">
                  <div className="text-center px-4">
                    <p className="text-white text-lg font-semibold mb-2 line-clamp-2">{getVideoUrl().title}</p>
                    <p className="text-white/70 text-sm mb-4">This video cannot be embedded due to creator restrictions</p>
                    <div className="flex gap-3 justify-center">
                      <a
                        href={getVideoUrl().watchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-full font-semibold transition"
                      >
                        <Play className="h-5 w-5" /> Watch on YouTube
                      </a>
                      <button
                        onClick={fetchNewVideo}
                        className="inline-flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-3 rounded-full font-semibold transition"
                      >
                        Try Another
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Auto-play YouTube video immediately */}
          {youtubeVideo && !embedFailed && (
            <iframe
              ref={iframeRef}
              src={`${getVideoUrl().url}&autoplay=1&mute=0&enablejsapi=1&origin=${window.location.origin}`}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              style={{ border: 'none' }}
              title={getVideoUrl().title || "Live Stream"}
              onLoad={() => {
                setVideoLoading(false);
              }}
              onError={() => {
                console.log('Video embed failed');
                setEmbedFailed(true);
                setVideoLoading(false);
              }}
            />
          )}
          
          {/* Overlay badges */}
          <div className="absolute top-4 left-4 flex items-center gap-3 z-10 pointer-events-none">
            <Badge className="bg-red-500 text-white animate-pulse shadow-lg">
              <Radio className="h-3 w-3 mr-1" /> LIVE
            </Badge>
            <Badge variant="secondary" className="bg-black/70 text-white backdrop-blur-sm">
              <Users className="h-3 w-3 mr-1" /> {realViewerCount || viewerCount} watching
            </Badge>
          </div>
          
          {/* Quick action buttons - Always visible on the video */}
          {youtubeVideo && !embedFailed && (
            <div className="absolute top-4 right-4 flex items-center gap-2 z-30">
              <a
                href={getVideoUrl().watchUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-red-600/90 hover:bg-red-700 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1 transition backdrop-blur-sm shadow-lg"
              >
                <Play className="h-3 w-3" /> YouTube
              </a>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  fetchNewVideo();  // Fetch a new video
                }}
                className="bg-black/70 hover:bg-black/90 text-white text-xs px-3 py-1.5 rounded-full transition backdrop-blur-sm shadow-lg"
              >
                Next
              </button>
            </div>
          )}
          
          {/* Video title overlay at bottom */}
          {youtubeVideo && !embedFailed && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 z-10">
              <p className="text-white text-sm font-medium truncate">{youtubeVideo.title}</p>
              <p className="text-white/60 text-xs">{youtubeVideo.channel_title}</p>
            </div>
          )}
        </div>
      )}

      {/* Real Stream Video Container */}
      {!isMockStream && (
        <div ref={remoteVideoRef} className="w-full h-full">
          {/* Connecting State */}
          {!isConnected && !connectionError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-white text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white mx-auto mb-4"></div>
                <p>Connecting to stream...</p>
              </div>
            </div>
          )}

          {/* Host Offline State */}
          {isConnected && connectionError && !hasRemoteVideo && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
              <div className="text-white text-center p-6">
                <Video className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <h3 className="text-xl font-semibold mb-2">Host is currently offline</h3>
                <p className="text-slate-400 mb-4">
                  The stream will start when the host goes live.
                </p>
                <p className="text-sm text-slate-500">
                  Check back soon or browse other live streams!
                </p>
              </div>
            </div>
          )}
          
          {/* Live Badge for real streams */}
          {hasRemoteVideo && (
            <div className="absolute top-4 left-4 flex items-center gap-3">
              <Badge className="bg-red-500 text-white animate-pulse">
                <Radio className="h-3 w-3 mr-1" /> LIVE
              </Badge>
              <Badge variant="secondary" className="bg-black/50 text-white">
                <Users className="h-3 w-3 mr-1" /> {viewerCount}
              </Badge>
            </div>
          )}
        </div>
      )}
      
      {/* Host Name for real streams */}
      {!isMockStream && (
        <div className="absolute bottom-4 left-4">
          <Badge variant="secondary" className="bg-black/50 text-white">
            {hostName}
          </Badge>
        </div>
      )}
      
      {/* Highlighted Product Overlay */}
      {highlightedProduct && (
        <div className="absolute bottom-16 left-4 right-4 bg-white/95 backdrop-blur rounded-lg p-3 shadow-lg animate-in slide-in-from-bottom z-20">
          <div className="flex items-center gap-3">
            {highlightedProduct.image && (
              <img src={highlightedProduct.image} alt="" className="w-16 h-16 rounded-lg object-cover" />
            )}
            <div className="flex-1">
              <p className="font-semibold text-sm line-clamp-1">{highlightedProduct.title}</p>
              <p className="text-emerald-600 font-bold">${highlightedProduct.price?.toLocaleString()}</p>
            </div>
            <Button 
              size="sm" 
              className="bg-emerald-500 hover:bg-emerald-600"
              onClick={() => onPurchase?.(highlightedProduct)}
            >
              <ShoppingBag className="h-4 w-4 mr-1" /> Buy Now
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Chat Component - Separate from video for flexibility
const StreamChat = ({ chatMessages, chatInput, setChatInput, sendMessage }) => (
  <div className="bg-white rounded-lg border flex flex-col h-[400px] lg:h-auto">
    <div className="p-3 border-b">
      <h3 className="font-semibold flex items-center gap-2">
        <MessageCircle className="h-4 w-4" /> Live Chat
      </h3>
    </div>
    
    <div className="flex-1 overflow-y-auto p-3 space-y-2">
      {chatMessages.map((msg, idx) => (
        <div key={idx} className="text-sm">
          <span className="font-semibold text-blue-600">{msg.sender_name}: </span>
          <span>{msg.content}</span>
        </div>
      ))}
    </div>
    
    <div className="p-3 border-t flex gap-2">
      <Input
        placeholder="Send a message..."
        value={chatInput}
        onChange={(e) => setChatInput(e.target.value)}
        onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
      />
      <Button size="icon" onClick={sendMessage}>
        <Send className="h-4 w-4" />
      </Button>
    </div>
  </div>
);

// Products Panel - Separate component
const StreamProducts = ({ products, onPurchase, highlightedProduct }) => {
  if (!products || products.length === 0) return null;
  
  return (
    <div className="lg:col-span-3">
      <h3 className="font-semibold mb-3 flex items-center gap-2">
        <ShoppingBag className="h-4 w-4" /> Products in this stream
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {products.map((product) => (
          <Card 
            key={product.listing_id} 
            className={`cursor-pointer hover:shadow-md transition ${
              highlightedProduct?.listing_id === product.listing_id ? 'ring-2 ring-emerald-500' : ''
            }`}
            onClick={() => onPurchase?.(product)}
          >
            {product.image && (
              <img src={product.image} alt="" className="w-full h-24 object-cover rounded-t-lg" />
            )}
            <CardContent className="p-2">
              <p className="text-xs font-medium line-clamp-1">{product.title}</p>
              <p className="text-sm font-bold text-emerald-600">${product.price}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Live Stream Card Component
export const LiveStreamCard = ({ stream, onClick }) => {
  const isLive = stream.status === 'live';
  const isScheduled = stream.status === 'scheduled';
  
  return (
    <Card 
      className="cursor-pointer hover:shadow-lg transition overflow-hidden group"
      onClick={onClick}
      data-testid={`stream-card-${stream.stream_id}`}
    >
      <div className="relative aspect-video bg-slate-100">
        {stream.products?.[0]?.image ? (
          <img 
            src={stream.products[0].image} 
            alt="" 
            className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-red-500 to-pink-500">
            <Video className="h-12 w-12 text-white/50" />
          </div>
        )}
        
        {/* Status Badge */}
        <div className="absolute top-2 left-2">
          {isLive ? (
            <Badge className="bg-red-500 text-white animate-pulse">
              <Radio className="h-3 w-3 mr-1" /> LIVE
            </Badge>
          ) : isScheduled ? (
            <Badge variant="secondary">
              <Calendar className="h-3 w-3 mr-1" /> Scheduled
            </Badge>
          ) : (
            <Badge variant="outline">Ended</Badge>
          )}
        </div>
        
        {/* Viewer Count */}
        {isLive && (
          <div className="absolute top-2 right-2">
            <Badge variant="secondary" className="bg-black/50 text-white">
              <Eye className="h-3 w-3 mr-1" /> {stream.viewer_count || 0}
            </Badge>
          </div>
        )}
        
        {/* Product Count */}
        {stream.products?.length > 0 && (
          <div className="absolute bottom-2 right-2">
            <Badge variant="secondary" className="bg-black/50 text-white">
              <Tag className="h-3 w-3 mr-1" /> {stream.products.length} items
            </Badge>
          </div>
        )}
      </div>
      
      <CardContent className="p-3">
        <h3 className="font-semibold line-clamp-1">{stream.title}</h3>
        <p className="text-sm text-muted-foreground">{stream.host_name}</p>
        
        {isScheduled && stream.scheduled_at && (
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {new Date(stream.scheduled_at).toLocaleString()}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

// Show Theme Options
const SHOW_THEMES = [
  { value: '', label: 'Optional' },
  { value: 'fashion_night', label: 'Fashion Night' },
  { value: 'designer_deals', label: 'Designer Deals' },
  { value: 'electronics_showcase', label: 'Electronics Showcase' },
  { value: 'vintage_finds', label: 'Vintage Finds' },
  { value: 'sneaker_drop', label: 'Sneaker Drop' },
  { value: 'jewelry_gems', label: 'Jewelry & Gems' },
  { value: 'home_decor', label: 'Home Decor' },
  { value: 'collectibles', label: 'Collectibles' },
  { value: 'sports_memorabilia', label: 'Sports Memorabilia' },
  { value: 'luxury_resale', label: 'Luxury Resale' },
  { value: 'clearance_sale', label: 'Clearance Sale' },
  { value: 'mystery_boxes', label: 'Mystery Boxes' },
];

// Create Stream Dialog - BidZuu Style
export const CreateStreamDialog = ({ open, onOpenChange, onCreated, userListings = [] }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [scheduledAt, setScheduledAt] = useState('');
  const [thumbnail, setThumbnail] = useState('');
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [theme, setTheme] = useState('');
  const [lowStarts, setLowStarts] = useState(false);
  const [acceptShared, setAcceptShared] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Convert HEIC to JPEG if needed
    let processedFile = file;
    if (isHeicFile(file)) {
      setUploading(true);
      try {
        processedFile = await convertHeicToJpeg(file);
      } catch (convError) {
        console.error('HEIC conversion error:', convError);
        setUploading(false);
        return;
      }
    }
    
    // Preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setThumbnailPreview(e.target.result);
    reader.readAsDataURL(processedFile);
    
    // Upload to server
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', processedFile);
      
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/upload/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setThumbnail(data.url);
      }
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleCreate = async () => {
    if (!title.trim()) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/live/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title,
          description,
          products: selectedProducts,
          scheduled_at: scheduledAt || null,
          thumbnail: thumbnail || null,
          theme: theme || null,
          low_starts: lowStarts,
          accept_shared: acceptShared
        })
      });
      
      if (res.ok) {
        const stream = await res.json();
        onCreated?.(stream);
        onOpenChange(false);
        // Reset form
        setTitle('');
        setDescription('');
        setSelectedProducts([]);
        setScheduledAt('');
        setThumbnail('');
        setThumbnailPreview('');
        setTheme('');
        setLowStarts(false);
        setAcceptShared(false);
      }
    } catch (error) {
      console.error('Error creating stream:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleProduct = (listingId) => {
    setSelectedProducts(prev => 
      prev.includes(listingId)
        ? prev.filter(id => id !== listingId)
        : [...prev, listingId]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-5 text-white">
          <DialogTitle className="flex items-center gap-3 text-xl font-bold">
            <div className="h-10 w-10 bg-white/20 rounded-full flex items-center justify-center">
              <Radio className="h-5 w-5" />
            </div>
            Create Live Show
          </DialogTitle>
          <p className="text-emerald-100 text-sm mt-1">
            Set up your live auction and start selling to your audience
          </p>
        </div>
        
        <div className="p-5 space-y-5">
          {/* Two Column Layout: Cover + Details */}
          <div className="flex gap-5">
            {/* Covershot Upload */}
            <div className="flex-shrink-0">
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_IMAGE_FORMATS}
                onChange={handleThumbnailUpload}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-28 h-36 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center hover:border-emerald-500 hover:bg-emerald-50 transition cursor-pointer overflow-hidden"
                disabled={uploading}
              >
                {thumbnailPreview ? (
                  <img src={thumbnailPreview} alt="Cover" className="w-full h-full object-cover" />
                ) : (
                  <>
                    {uploading ? (
                      <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
                    ) : (
                      <>
                        <Video className="h-6 w-6 text-slate-400 mb-1" />
                        <span className="text-xs text-slate-500 text-center px-2">Add Cover</span>
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
            
            {/* Title & Theme */}
            <div className="flex-1 space-y-3">
              <div>
                <Input
                  placeholder="Show title..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="font-medium text-base"
                  data-testid="show-title-input"
                />
              </div>
              <select
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
                className="w-full h-9 px-3 border rounded-md bg-background text-sm"
                data-testid="show-theme-select"
              >
                {SHOW_THEMES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Schedule */}
          <div className="bg-slate-50 rounded-xl p-4">
            <label className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4" />
              When do you want to go live?
            </label>
            <Input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              className="bg-white"
              data-testid="show-schedule-input"
            />
            <p className="text-xs text-slate-500 mt-1">Leave empty to go live immediately</p>
          </div>
          
          {/* Toggles Row */}
          <div className="grid grid-cols-2 gap-3">
            {/* Low Starts */}
            <button
              onClick={() => setLowStarts(!lowStarts)}
              className={`p-3 rounded-xl border-2 text-left transition ${
                lowStarts 
                  ? 'border-emerald-500 bg-emerald-50' 
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              data-testid="low-starts-toggle"
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-emerald-600">$5</span>
                <div className={`w-10 h-5 rounded-full transition ${lowStarts ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                  <div className={`w-4 h-4 bg-white rounded-full shadow mt-0.5 transition-all ${lowStarts ? 'ml-5' : 'ml-0.5'}`} />
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-1">Low Starts</p>
            </button>
            
            {/* Accept Shared */}
            <button
              onClick={() => setAcceptShared(!acceptShared)}
              className={`p-3 rounded-xl border-2 text-left transition ${
                acceptShared 
                  ? 'border-emerald-500 bg-emerald-50' 
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              data-testid="accept-shared-toggle"
            >
              <div className="flex items-center justify-between">
                <Users className="h-5 w-5 text-blue-500" />
                <div className={`w-10 h-5 rounded-full transition ${acceptShared ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                  <div className={`w-4 h-4 bg-white rounded-full shadow mt-0.5 transition-all ${acceptShared ? 'ml-5' : 'ml-0.5'}`} />
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-1">Community Items</p>
            </button>
          </div>
          
          {/* Products Selection */}
          {userListings.length > 0 && (
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Select items to showcase ({selectedProducts.length})
              </label>
              <div className="grid grid-cols-5 gap-2 max-h-32 overflow-y-auto p-2 border rounded-xl bg-slate-50">
                {userListings.map((listing) => (
                  <button
                    key={listing.listing_id}
                    onClick={() => toggleProduct(listing.listing_id)}
                    className={`relative rounded-lg overflow-hidden transition ${
                      selectedProducts.includes(listing.listing_id)
                        ? 'ring-2 ring-emerald-500 ring-offset-1'
                        : 'opacity-70 hover:opacity-100'
                    }`}
                  >
                    {listing.images?.[0] && (
                      <img 
                        src={listing.images[0]} 
                        alt="" 
                        className="w-full aspect-square object-cover"
                      />
                    )}
                    {selectedProducts.includes(listing.listing_id) && (
                      <div className="absolute top-1 right-1 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Action Button */}
          <Button 
            onClick={handleCreate} 
            className={`w-full h-12 text-base font-semibold ${
              scheduledAt 
                ? 'bg-blue-500 hover:bg-blue-600' 
                : 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700'
            }`}
            disabled={!title.trim() || loading}
            data-testid="schedule-show-btn"
          >
            {loading ? (
              <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
            ) : scheduledAt ? (
              <>
                <Calendar className="h-5 w-5 mr-2" />
                Schedule Show
              </>
            ) : (
              <>
                <Radio className="h-5 w-5 mr-2 animate-pulse" />
                Go Live Now
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default { StreamBroadcaster, StreamViewer, LiveStreamCard, CreateStreamDialog };
